import { useCallback, useEffect, useState } from 'react';
import {
  AddMedicine, Confirm, toast,
  type ConsentState, type Destructive, type MedicineParse, type MedicineTransport,
} from '@drbell/ui';
import { addDaysToLocalDate, courseDates, localDateAt } from '@drbell/core';
import { api, ApiError, type MedicationEntry, type PatientSummary, type PrescriptionPhoto } from '../api.ts';

/**
 * Their medicines — RX-CARE-14, parity with the phone.
 *
 * What is prescribed, how it is taken as the doctor wrote it, and how many
 * are left. A caregiver who buys the strips is the person who most needs
 * "8 left" and was the one person who could not see it without the phone.
 *
 * Adding a medicine uses the same dialog as the organisation console: the
 * doctor's shorthand read back in a sentence to check against the slip, and a
 * photograph of the strip and of the prescription.
 *
 * Stopping follows the doctor. DrBell records that a medicine was stopped;
 * it never suggests stopping one, and the confirmation says so.
 */
const FOOD: Record<string, string> = {
  before_food: 'before food',
  with_food: 'with food',
  after_food: 'after food',
  empty_stomach: 'on an empty stomach',
};

const reason = (e: unknown) => (e instanceof ApiError ? e.message : e instanceof Error ? e.message : String(e));

export function Medicines({ patient, canEdit }: { patient: PatientSummary; canEdit: boolean }) {
  const [items, setItems] = useState<MedicationEntry[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [pending, setPending] = useState<Destructive | null>(null);
  const [adding, setAdding] = useState(false);
  const [slips, setSlips] = useState<PrescriptionPhoto[]>([]);

  const load = useCallback(async () => {
    try {
      setItems((await api.get<{ medications: MedicationEntry[] }>(`/api/patients/${patient.id}/medications`)).medications);
      setSlips(
        (await api.get<{ prescriptions: PrescriptionPhoto[] }>(`/api/patients/${patient.id}/prescriptions`).catch(() => ({
          prescriptions: [],
        }))).prescriptions,
      );
    } catch (e) {
      setError(reason(e));
    }
  }, [patient.id]);

  /** The shared "Add a medicine" dialog, on this app's client. */
  const transport: MedicineTransport = {
    parse: (input, unit) => api.post<MedicineParse>('/api/notation/parse', { input, unit: unit ?? 'tablet' }),
    create: (body) => api.post(`/api/patients/${patient.id}/medications`, body),
    consent: () => api.get<ConsentState>('/api/consent'),
    acceptConsent: (documentId, version) => api.post('/api/consent/accept', { documentId, version }),
    uploader: (kind) => ({
      upload: (file) => api.upload<{ key: string; url: string }>(`/api/patients/${patient.id}/media?kind=${kind}`, file),
    }),
    // Their dates, and an inclusive end: "x 7d" is seven days (defect 136).
    courseDates: (days) => courseDates(days, patient.timezone),
  };

  useEffect(() => {
    setItems(null);
    void load();
  }, [load]);

  const stop = (m: MedicationEntry['medication']) =>
    setPending({
      title: `Stop reminders for ${m.name}?`,
      body: `Reminders for ${m.name} stop now, for ${patient.displayName} and for everyone looking after them. What has been recorded is kept.`,
      reassurance: 'Only stop a medicine when the doctor has said to.',
      confirmLabel: 'Stop reminders',
      onConfirm: async () => {
        setPending(null);
        try {
          await api.del(`/api/patients/${patient.id}/medications/${m.id}`);
          toast.success(`${m.name} stopped`);
          await load();
        } catch (e) {
          toast.error('Could not stop it', reason(e));
        }
      },
    });

  if (error) return <p role="alert" className="rounded-xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-500">{error}</p>;
  if (!items) return <p className="text-(--muted)">Loading…</p>;

  return (
    <section className="space-y-4">
      {items.length === 0 ? (
        <p className="rounded-xl border border-dashed border-(--line) px-4 py-8 text-center text-(--muted)">
          No medicines yet.
        </p>
      ) : (
        <ul className="space-y-3">
          {items.map(({ medication: m, schedules }) => {
            const live = schedules.filter((s) => s.archivedAt === null);
            return (
              <li key={m.id} className="rounded-xl border border-(--line) p-4">
                <div className="flex flex-wrap items-start gap-3">
                  <div className="min-w-0 flex-1">
                    <p className="font-medium">
                      {m.name}
                      {m.strength && <span className="text-(--muted)"> {m.strength}{m.strengthUnit}</span>}
                      {m.isCritical && (
                        <span className="ml-2 rounded-full border border-red-500/40 px-2 py-0.5 text-xs text-red-500">critical</span>
                      )}
                    </p>
                    {m.brandName && m.brandName !== m.name && <p className="text-sm text-(--muted)">{m.brandName}</p>}
                    {live.map((s) => (
                      <p key={s.id} className="text-sm text-(--muted)">
                        {/* The doctor's shorthand verbatim — never translated. */}
                        {s.sourceNotation && <span className="font-mono">{s.sourceNotation}</span>}
                        {s.foodRelation && FOOD[s.foodRelation] && <> · {FOOD[s.foodRelation]}</>}
                        {s.type === 'prn' && ' · only when needed'}
                      </p>
                    ))}
                    {m.instructionsText && <p className="mt-1 text-sm">{m.instructionsText}</p>}
                  </div>
                  {canEdit && (
                    <button
                      onClick={() => stop(m)}
                      className="min-h-11 rounded-lg border border-(--line) px-3 text-sm text-(--muted) hover:border-red-500 hover:text-red-500"
                    >
                      Stop…
                    </button>
                  )}
                </div>
                <Pause patient={patient} medication={m} canEdit={canEdit} onChanged={load} />
                <Course patient={patient} medicationId={m.id} name={m.name} schedules={live} canEdit={canEdit} onChanged={load} />
                <Stock patientId={patient.id} medication={m} canEdit={canEdit} onChanged={load} />
              </li>
            );
          })}
        </ul>
      )}
      {canEdit && (
        <button
          onClick={() => setAdding(true)}
          className="min-h-11 rounded-lg bg-(--accent) px-4 text-sm font-medium text-white"
        >
          + Add a medicine
        </button>
      )}

      {slips.length > 0 && (
        <div className="rounded-xl border border-(--line) p-4">
          <h2 className="mb-2 text-sm font-medium">Prescriptions</h2>
          <ul className="flex flex-wrap gap-3">
            {slips.map((p) => (
              <li key={p.id}>
                <a href={p.url} target="_blank" rel="noopener" className="block">
                  <img src={p.url} alt={`Prescription${p.doctorName ? ` from ${p.doctorName}` : ''}`} className="h-24 w-24 rounded-lg border border-(--line) object-cover" />
                </a>
                <p className="mt-1 max-w-24 truncate text-xs text-(--muted)">
                  {p.doctorName ?? new Date(p.createdAt).toLocaleDateString()}
                </p>
                {canEdit && (
                  <button
                    onClick={() =>
                      setPending({
                        title: 'Remove this prescription photo?',
                        body: 'The photograph is deleted. The medicines added with it are not affected.',
                        confirmLabel: 'Remove photo',
                        onConfirm: async () => {
                          setPending(null);
                          try {
                            await api.del(`/api/patients/${patient.id}/prescriptions/${p.id}`);
                            toast.success('Photo removed');
                            await load();
                          } catch (e) {
                            toast.error('Could not remove it', e instanceof Error ? e.message : String(e));
                          }
                        },
                      })
                    }
                    className="text-xs text-(--muted) hover:text-red-500"
                  >
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}

      {adding && (
        <AddMedicine
          personName={patient.displayName}
          transport={transport}
          onClose={() => setAdding(false)}
          onAdded={async () => { setAdding(false); await load(); }}
        />
      )}
      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </section>
  );
}

/**
 * How many are left — RX-MED-11.
 *
 * "Count" replaces the number (after counting the strip); "New strip" adds to
 * it. The answer includes how long that lasts, which is what people plan by.
 */
function Stock({
  patientId,
  medication: m,
  canEdit,
  onChanged,
}: {
  patientId: string;
  medication: MedicationEntry['medication'];
  canEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  const [value, setValue] = useState('');
  const [busy, setBusy] = useState(false);
  const unit = m.stockUnit ?? 'left';
  const n = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(n) && n >= 0 && n <= 10_000;

  const save = async (mode: 'set' | 'add') => {
    setBusy(true);
    try {
      const r = await api.put<{
        stockCount: number; daysRemaining: number | null;
        outlook?: 'nothing_scheduled' | 'runs_out' | 'covers_course' | 'beyond'; atLeastDays?: number | null;
      }>(
        `/api/patients/${patientId}/medications/${m.id}/stock`,
        { mode, count: n },
      );
      toast.success(
        `${r.stockCount} ${unit === 'left' ? 'left' : unit}`,
        r.daysRemaining !== null
          ? `About ${r.daysRemaining} day${r.daysRemaining === 1 ? '' : 's'} at the current schedule.`
          : r.outlook === 'covers_course'
            ? 'Enough to finish the course.'
            : r.outlook === 'beyond' && r.atLeastDays
              ? `Lasts at least ${r.atLeastDays} days.`
              : undefined,
      );
      setValue('');
      await onChanged();
    } catch (e) {
      toast.error('Could not save the count', reason(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="mt-3 flex flex-wrap items-center gap-2 border-t border-(--line) pt-3 text-sm">
      <span className={m.stockCount !== null && m.stockCount <= 5 ? 'font-medium text-amber-600' : 'text-(--muted)'}>
        {m.stockCount === null ? 'Not counted' : `${m.stockCount} ${unit === 'left' ? 'left' : `${unit} left`}`}
      </span>
      {canEdit && (
        <div className="flex w-full flex-wrap gap-2 sm:ml-auto sm:w-auto">
          <input
            value={value}
            onChange={(e) => setValue(e.target.value)}
            inputMode="numeric"
            placeholder="How many"
            aria-label={`How many ${m.name}`}
            className="w-28 rounded-lg border border-(--line) bg-(--bg) min-h-11 px-3"
          />
          <button disabled={!valid || busy} onClick={() => void save('set')} className="min-h-11 rounded-lg border border-(--line) px-3 disabled:opacity-40">
            That&rsquo;s how many
          </button>
          <button disabled={!valid || busy || n === 0} onClick={() => void save('add')} className="min-h-11 rounded-lg border border-(--line) px-3 disabled:opacity-40">
            Add a new strip
          </button>
        </div>
      )}
    </div>
  );
}

/** "Sat 26 Sep", for a calendar date — no time zone involved. */
const dayName = (d: string) =>
  new Date(`${d}T00:00:00Z`).toLocaleDateString('en-IN', { weekday: 'short', day: 'numeric', month: 'short', timeZone: 'UTC' });

const PAUSE_REASONS = [
  ['hospital', 'In hospital'],
  ['travel', 'Travelling'],
  ['doctor', 'The doctor said to hold it'],
  ['other', 'Another reason'],
] as const;
type PauseReason = (typeof PAUSE_REASONS)[number][0];
const PAUSE_SAID: Record<PauseReason, string> = {
  hospital: 'in hospital', travel: 'travelling', doctor: 'the doctor said to hold it', other: 'another reason',
};

/**
 * Pause and resume — docs/33 C5, parity with the phone.
 *
 * In hospital, travelling, or the doctor said to hold it: no reminders, and
 * nobody told it was missed, on the paused days, until a last day or until
 * someone resumes it. The medicine and its history stay as they are.
 */
function Pause({ patient, medication: m, canEdit, onChanged }: {
  patient: PatientSummary; medication: MedicationEntry['medication']; canEdit: boolean; onChanged: () => void;
}) {
  const [open, setOpen] = useState(false);
  const [why, setWhy] = useState<PauseReason>('hospital');
  /** Days from today, or null for until someone resumes it. */
  const [days, setDays] = useState<number | null>(7);
  const [busy, setBusy] = useState(false);
  const today = localDateAt(Date.now(), patient.timezone);
  const until = days === null ? null : addDaysToLocalDate(today, days - 1);
  const day = (d: string) => new Intl.DateTimeFormat('en-IN', { weekday: 'long', day: 'numeric', month: 'long', timeZone: 'UTC' }).format(new Date(`${d}T12:00:00Z`));

  const send = async (path: 'pause' | 'resume', body?: unknown) => {
    setBusy(true);
    try {
      await api.post(`/api/patients/${patient.id}/medications/${m.id}/${path}`, body ?? {});
      toast.success(path === 'pause' ? `${m.name} paused` : `${m.name} resumed`);
      setOpen(false);
      onChanged();
    } catch (e) {
      toast.error(path === 'pause' ? 'Could not pause it' : 'Could not resume it', reason(e));
    } finally {
      setBusy(false);
    }
  };

  if (m.pausedAt) {
    return (
      <div className="mt-3 flex flex-wrap items-center gap-3 rounded-lg border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-sm">
        <span className="font-semibold tracking-wide">PAUSED</span>
        <span className="text-(--muted)">
          {m.pausedUntil ? `to ${day(m.pausedUntil)}` : 'until resumed'}
          {m.pauseReason ? ` · ${PAUSE_SAID[m.pauseReason]}` : ''}
        </span>
        {canEdit && (
          <button disabled={busy} onClick={() => void send('resume')} className="ml-auto min-h-11 rounded-lg border border-(--line) px-3 hover:border-(--accent) disabled:opacity-40">
            Resume now
          </button>
        )}
      </div>
    );
  }
  if (!canEdit) return null;
  if (!open) {
    return (
      <div className="mt-3">
        <button onClick={() => setOpen(true)} className="min-h-11 rounded-lg border border-(--line) px-3 text-sm hover:border-(--accent)">
          Pause…
        </button>
      </div>
    );
  }
  return (
    <div className="mt-3 space-y-3 rounded-lg border border-(--line) p-3 text-sm">
      <p className="font-medium">Pause {m.name}?</p>
      <p className="text-(--muted)">
        No reminders, and nobody is told it was missed, on the paused days. Only when the doctor or the situation says so.
      </p>
      <fieldset className="flex flex-wrap gap-2">
        <legend className="sr-only">Why</legend>
        {PAUSE_REASONS.map(([value, label]) => (
          <label key={value} className={`min-h-11 cursor-pointer rounded-lg border px-3 py-2 ${why === value ? 'border-(--accent)' : 'border-(--line)'}`}>
            <input type="radio" name={`why-${m.id}`} value={value} checked={why === value} onChange={() => setWhy(value)} className="sr-only" />
            {label}
          </label>
        ))}
      </fieldset>
      <fieldset className="flex flex-wrap gap-2">
        <legend className="sr-only">For how long</legend>
        {[3, 7, 14].map((n) => (
          <label key={n} className={`min-h-11 cursor-pointer rounded-lg border px-3 py-2 ${days === n ? 'border-(--accent)' : 'border-(--line)'}`}>
            <input type="radio" name={`days-${m.id}`} checked={days === n} onChange={() => setDays(n)} className="sr-only" />
            {n} days, from today
          </label>
        ))}
        <label className={`min-h-11 cursor-pointer rounded-lg border px-3 py-2 ${days === null ? 'border-(--accent)' : 'border-(--line)'}`}>
          <input type="radio" name={`days-${m.id}`} checked={days === null} onChange={() => setDays(null)} className="sr-only" />
          Until someone resumes it
        </label>
      </fieldset>
      <p>{until ? `No reminders up to and including ${day(until)}.` : 'No reminders until someone resumes it.'}</p>
      <div className="flex gap-2">
        <button onClick={() => setOpen(false)} className="min-h-11 rounded-lg border border-(--line) px-3">Cancel</button>
        <button disabled={busy} onClick={() => void send('pause', { until, reason: why })} className="min-h-11 rounded-lg bg-(--accent) px-3 font-medium text-white disabled:opacity-40">
          Pause it
        </button>
      </div>
    </div>
  );
}

/**
 * How long to take it — RX-MED-12.
 *
 * The course's last day, or that it has none; and, for a guardian, recording
 * a change the doctor made. The last day is included, can be today at the
 * earliest (a day already past is stopping, and Stop says so), and "no end"
 * means until the doctor says to stop.
 */
function Course({
  patient, medicationId, name, schedules, canEdit, onChanged,
}: {
  patient: PatientSummary;
  medicationId: string;
  name: string;
  schedules: MedicationEntry['schedules'];
  canEdit: boolean;
  onChanged: () => Promise<void>;
}) {
  const current = schedules[0]?.endDate ?? null;
  const today = new Intl.DateTimeFormat('en-CA', {
    timeZone: patient.timezone, year: 'numeric', month: '2-digit', day: '2-digit',
  }).format(Date.now());
  const [open, setOpen] = useState(false);
  const [lastDay, setLastDay] = useState(current && current >= today ? current : today);
  const [noEnd, setNoEnd] = useState(current === null);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [pending, setPending] = useState<Destructive | null>(null);
  if (schedules.length === 0) return null;

  const next = noEnd ? null : lastDay;
  const valid = noEnd || (/^\d{4}-\d{2}-\d{2}$/.test(lastDay) && lastDay >= today);

  const save = () =>
    setPending({
      title: `Change how long to take ${name}?`,
      body: next
        ? `Reminders stop after ${dayName(next)}, for ${patient.displayName} and everyone looking after them.`
        : `Reminders carry on until the medicine is stopped.`,
      reassurance: 'Only do this if the doctor said so. What has been recorded is kept.',
      confirmLabel: 'Record the change',
      onConfirm: async () => {
        setPending(null);
        setBusy(true);
        try {
          await api.put(`/api/patients/${patient.id}/medications/${medicationId}/course`, {
            endDate: next,
            ...(note.trim() ? { note: note.trim() } : {}),
          });
          toast.success(next ? `Now until ${dayName(next)}` : 'Now with no end date');
          setOpen(false);
          await onChanged();
        } catch (e) {
          toast.error('Could not change it', reason(e));
        } finally {
          setBusy(false);
        }
      },
    });

  return (
    <div className="mt-3 border-t border-(--line) pt-3 text-sm">
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-(--muted)">{current ? `Until ${dayName(current)}` : 'No end date'}</span>
        {canEdit && !open && (
          <button onClick={() => setOpen(true)} className="ml-auto min-h-11 rounded-lg border border-(--line) px-3 hover:border-(--accent)">
            The doctor changed how long…
          </button>
        )}
      </div>
      {open && (
        <div className="mt-2 space-y-2 rounded-lg border border-(--line) p-3">
          <p className="text-xs text-(--muted)">Only change this when the doctor has told you to.</p>
          <label className="flex min-h-11 items-center gap-2">
            <input type="radio" checked={!noEnd} onChange={() => setNoEnd(false)} />
            <span>Last day</span>
            <input
              type="date"
              value={lastDay}
              min={today}
              disabled={noEnd}
              onChange={(e) => setLastDay(e.target.value)}
              className="rounded-lg border border-(--line) bg-(--bg) min-h-11 px-2 disabled:opacity-40"
            />
          </label>
          <label className="flex min-h-11 items-center gap-2">
            <input type="radio" checked={noEnd} onChange={() => setNoEnd(true)} />
            <span>No end — until the doctor says to stop</span>
          </label>
          <input
            value={note}
            onChange={(e) => setNote(e.target.value)}
            maxLength={300}
            placeholder="What the doctor said (optional)"
            aria-label="What the doctor said"
            className="w-full rounded-lg border border-(--line) bg-(--bg) min-h-11 px-3"
          />
          <div className="flex gap-2">
            <button onClick={() => setOpen(false)} className="min-h-11 rounded-lg border border-(--line) px-3">Cancel</button>
            <button
              disabled={!valid || busy || next === current}
              onClick={save}
              className="min-h-11 rounded-lg bg-(--accent) px-3 font-medium text-white disabled:opacity-40"
            >
              {busy ? 'Updating reminders…' : 'Save'}
            </button>
          </div>
        </div>
      )}
      {pending && <Confirm {...pending} onCancel={() => setPending(null)} />}
    </div>
  );
}

